<script setup>
import { useRoute } from "vue-router"
import { onMounted, ref } from "vue"
import { useMutation } from "@vue/apollo-composable"
import Login from "@/pages/auth/Login.vue"
import VERIFY_USER_EMAIL from "@/graphql/Auth/VerifyUserEmailMutation.gql"

const route = useRoute()

const token = ref(route.params.token)

// verify email mutation
const {
  mutate: verifyEmail,
  loading: verifyEmailLoading,
  onDone,
  onError,
} = useMutation(VERIFY_USER_EMAIL)

const verifyUserEmail = () => {
  verifyEmail({
    input: {
      token: token.value,
    },
  })
    .then(response => {
      console.log("Email verified:", response.data)
      const status = response.data.verifyUserEmail.status
      loadingMessage.value = loadedMessage.value[status]
    })
    .catch(error => {
      console.error("Verification failed:", error)
      const status = response.data.verifyUserEmail.status
      loadingMessage.value = "USER_NOT_FOUND"
    })
}

onMounted(() => {
  if (token.value) {
    verifyUserEmail()
  }
})

const loadingMessage = ref({
  status: null,
  title: "Validating...",
  headline: null,
  text: null,
  loginInput: false,
})

const loadedMessage = ref({
  TOKEN_NOT_FOUND: {
    title: undefined,
    headline: "Something went wrong.",
    text: "Please try logging in again. If you're still experiencing problems, contact us at contact@ytz.com",
    loginInput: false,
    status: "error",
  },
  USER_NOT_FOUND: {
    title: "Something went wrong",
    headline: "Please try logging in again.",
    text: "If you're still experiencing problems, contact us at contact@ytz.com",
    loginInput: false,
    status: "error",
  },
  EMAIL_VERIFY_SUCCESS: {
    title: "Email Verified!",
    headline: undefined,
    text: "Log in to get started",
    loginInput: true,
    status: "success",
  },
})
</script>

<template>
  <div
    class="flex h-screen flex-1 flex-col items-center justify-center px-6 py-12 lg:px-8 overflow-hidden"
  >
    <div class="flex flex-col content-container min-h-[400px]">
      <div
        class="text-2xl font-bold mb-2"
        :class="{
          'text-red-800': loadingMessage.status == 'error',
        }"
      >
        {{ loadingMessage.title }}
      </div>
      <div>
        <p
          class="mb-2 font-light"
          :class="{
            'text-red-700': loadingMessage.status == 'error',
          }"
        >
          {{ loadingMessage.headline }}
        </p>
        <p class="mt-1">{{ loadingMessage.text }}</p>
        <div class="divider mb-0"></div>
      </div>

      <!-- skeleton loading -->
      <div
        class="flex flex-col mt-10"
        :class="{
          invisible: !verifyEmailLoading && loadedMessage.loginInput,
          hidden: !verifyEmailLoading && !loadedMessage.loginInput,
        }"
      >
        <div class="skeleton h-12 w-full rounded-lg mb-4"></div>
        <div class="skeleton h-12 w-full rounded-lg"></div>
        <div class="divider"></div>
        <div class="skeleton h-12 w-full rounded-lg"></div>
      </div>

      <!-- login fields on success -->
      <div
        v-if="loadingMessage.loginInput"
        :class="{
          'fade-in': loadingMessage.loginInput,
        }"
      >
        <Login height="h-full" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.content-container {
  @apply w-[400px] p-6 rounded-lg bg-base-100 border border-neutral/70;
}
</style>
